import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import type { EducationSchool } from '../../types';
import educationService from '../../services/educationService';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '700px',
    width: '90vw',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    padding: '16px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorBrandForeground1,
    marginBottom: '8px',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
  },
  fieldLabel: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    marginBottom: '4px',
  },
  fieldValue: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word',
  },
});

interface SchoolDetailsProps {
  schoolId: string | null;
  open: boolean;
  onClose: () => void;
}

export const SchoolDetails: React.FC<SchoolDetailsProps> = ({
  schoolId,
  open,
  onClose,
}) => {
  const styles = useStyles();
  const [school, setSchool] = useState<EducationSchool | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && schoolId) {
      loadSchoolDetails();
    }
  }, [open, schoolId]);

  const loadSchoolDetails = async () => {
    if (!schoolId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await educationService.getSchoolById(schoolId);
      setSchool(response);
    } catch (err: any) {
      console.error('Error loading school details:', err);
      setError(err.message || 'Erro ao carregar detalhes da escola');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (label: string, value: any) => {
    return (
      <div>
        <div className={styles.fieldLabel}>{label}</div>
        <div className={styles.fieldValue}>{value || '-'}</div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            }
          >
            Detalhes da Escola
          </DialogTitle>

          <DialogContent>
            {loading && (
              <div className={styles.loading}>
                <Spinner label="Carregando detalhes..." />
              </div>
            )}

            {error && (
              <div className={styles.error}>
                <Text>Erro: {error}</Text>
              </div>
            )}

            {!loading && !error && school && (
              <div className={styles.content}>
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Informações Gerais</div>
                  <div className={styles.fieldRow}>
                    {renderField('Nome', school.displayName)}
                    {renderField('Número da Escola', school.schoolNumber)}
                  </div>
                  <div className={styles.fieldRow}>
                    {renderField('ID Externo', school.externalId)}
                    {renderField('ID Microsoft', school.id)}
                  </div>
                </div>

                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Direção</div>
                  <div className={styles.fieldRow}>
                    {renderField('Nome do Diretor', school.principalName)}
                    {renderField('Email do Diretor', school.principalEmail)}
                  </div>
                </div>

                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Contato</div>
                  <div className={styles.fieldRow}>
                    {renderField('Telefone', school.phone)}
                    {renderField('Fax', school.fax)}
                  </div>
                  {school.address && (
                    <div>
                      {renderField('Endereço', school.address)}
                    </div>
                  )}
                </div>

                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Informações de Sincronização</div>
                  <div className={styles.fieldRow}>
                    {renderField('Data de Criação', school.createdDateTime ? new Date(school.createdDateTime).toLocaleString('pt-BR') : '-')}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>

          <DialogActions>
            <Button appearance="primary" onClick={onClose}>
              Fechar
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SchoolDetails;
